import mixpanel from 'mixpanel-browser';

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN;
const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT?.trim() || 'unknown';
const DEDUPE_PREFIX = `mixpanel:lifecycle:${ENVIRONMENT}:`;

let initialized = false;
let identifiedUserId: string | undefined;

export type MixpanelProperties = Record<string, string | number | boolean | undefined>;

const localTimestamp = () => {
  const date = new Date();
  const pad = (value: number) => String(value).padStart(2, '0');

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const getUtmSource = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('utm_source') ?? params.get('source') ?? 'unknown';
};

export const initializeMixpanel = () => {
  if (initialized || typeof window === 'undefined' || !MIXPANEL_TOKEN) {
    return;
  }
  mixpanel.init(MIXPANEL_TOKEN, {
    debug: ENVIRONMENT.toUpperCase() === 'DEV',
    track_pageview: false,
    persistence: 'localStorage',
    api_host: 'https://api-eu.mixpanel.com',
    autocapture: false,
  });
  mixpanel.register({ environment: ENVIRONMENT });
  initialized = true;
};

export const identifyMixpanelUser = (userId: string, properties?: MixpanelProperties) => {
  initializeMixpanel();
  if (!initialized) {
    return;
  }

  if (identifiedUserId !== userId) {
    mixpanel.identify(userId);
    identifiedUserId = userId;
  }

  mixpanel.people.set({ ...properties, environment: ENVIRONMENT });
};

export const resetMixpanelUser = () => {
  initializeMixpanel();
  if (!initialized) {
    return;
  }

  mixpanel.reset();
  mixpanel.register({ environment: ENVIRONMENT });
  identifiedUserId = undefined;
};

export const trackMixpanelEvent = (eventName: string, properties: MixpanelProperties = {}) => {
  initializeMixpanel();
  if (!initialized) {
    return false;
  }

  return Boolean(
    mixpanel.track(eventName, {
      ...properties,
      environment: ENVIRONMENT,
      timestamp_local: properties.timestamp_local ?? localTimestamp(),
    }),
  );
};

export const trackMixpanelLifecycleEvent = (
  eventName: string,
  userId: string,
  properties: MixpanelProperties = {},
) => {
  const key = `${DEDUPE_PREFIX}${userId}:${eventName}`;
  if (window.localStorage.getItem(key)) {
    return;
  }

  trackMixpanelEvent(eventName, properties);
  window.localStorage.setItem(key, '1');
};

export const trackMixpanelOnce = (
  eventName: string,
  properties: MixpanelProperties = {},
  dedupeKey = eventName,
) => {
  initializeMixpanel();
  const distinctId = initialized ? mixpanel.get_distinct_id() : 'anonymous';
  const key = `${DEDUPE_PREFIX}${distinctId}:${dedupeKey}`;
  if (window.localStorage.getItem(key)) {
    return;
  }

  trackMixpanelEvent(eventName, properties);
  window.localStorage.setItem(key, '1');
};

export const getSignupSource = getUtmSource;
export const getLocalTimestamp = localTimestamp;
